import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  ColumnFiltersState,
  getFilteredRowModel,
  RowSelectionState,
  OnChangeFn,
} from '@tanstack/react-table';
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DataTablePagination } from './DataTablePagination';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { useState } from 'react';

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];

  // Loading state
  isLoading?: boolean;

  // Pagination - object form
  pagination?: {
    pageIndex: number;
    pageSize: number;
  };
  onPaginationChange?: (pagination: { pageIndex: number; pageSize: number }) => void;

  // Pagination - simple form (alternative to pagination object)
  pageIndex?: number;
  pageSize?: number;
  pageCount?: number;
  totalItems?: number;
  onPageChange?: (pageIndex: number) => void;
  onPageSizeChange?: (pageSize: number) => void;

  manualPagination?: boolean;

  // Sorting
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  manualSorting?: boolean;

  // Selection
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  enableRowSelection?: boolean;

  // Empty state
  emptyMessage?: string;

  // Row click
  onRowClick?: (row: TData) => void;

  // Filtering
  columnFilters?: ColumnFiltersState;
  onColumnFiltersChange?: OnChangeFn<ColumnFiltersState>;

  // Custom row ID
  getRowId?: (row: TData) => string;

  // Sticky header - enables fixed table header with scrollable body
  stickyHeader?: boolean;
  // Max height for the table container when stickyHeader is enabled (CSS value, e.g., "500px" or "calc(100vh - 300px)")
  maxHeight?: string;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  isLoading = false,
  pagination,
  onPaginationChange,
  // Simple pagination props
  pageIndex: simplePageIndex,
  pageSize: simplePageSize,
  pageCount,
  totalItems,
  onPageChange,
  onPageSizeChange,
  manualPagination = false,
  sorting: externalSorting,
  onSortingChange,
  manualSorting = false,
  rowSelection: externalRowSelection,
  onRowSelectionChange,
  enableRowSelection = false,
  emptyMessage = 'No results found.',
  onRowClick,
  columnFilters: externalColumnFilters,
  onColumnFiltersChange,
  getRowId,
  stickyHeader = false,
  maxHeight = 'calc(100vh - 320px)',
}: DataTableProps<TData, TValue>) {
  // Internal state for uncontrolled mode
  const [internalSorting, setInternalSorting] = useState<SortingState>([]);
  const [internalRowSelection, setInternalRowSelection] = useState<RowSelectionState>({});
  const [internalColumnFilters, setInternalColumnFilters] = useState<ColumnFiltersState>([]);

  // Use external state if provided, otherwise use internal
  const sorting = externalSorting ?? internalSorting;
  const rowSelection = externalRowSelection ?? internalRowSelection;
  const columnFilters = externalColumnFilters ?? internalColumnFilters;

  // Resolve pagination from either object form or simple props
  const resolvedPagination = pagination ?? (
    (simplePageIndex !== undefined || simplePageSize !== undefined)
      ? { pageIndex: simplePageIndex ?? 0, pageSize: simplePageSize ?? 10 }
      : undefined
  );

  // Determine if using simple pagination callbacks
  const useSimplePagination = !onPaginationChange && (onPageChange || onPageSizeChange);

  // Determine if manual pagination should be enabled
  const shouldUseManualPagination = manualPagination || pageCount !== undefined || useSimplePagination;

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),

    // Pagination
    ...(shouldUseManualPagination
      ? {
          manualPagination: true,
          pageCount: pageCount ?? -1,
        }
      : {
          getPaginationRowModel: getPaginationRowModel(),
        }),

    // Sorting
    ...(manualSorting
      ? { manualSorting: true }
      : { getSortedRowModel: getSortedRowModel() }),
    onSortingChange: onSortingChange ?? setInternalSorting,

    // Filtering
    getFilteredRowModel: getFilteredRowModel(),
    onColumnFiltersChange: onColumnFiltersChange ?? setInternalColumnFilters,

    // Selection
    enableRowSelection,
    onRowSelectionChange: onRowSelectionChange ?? setInternalRowSelection,

    // Row ID
    getRowId,

    state: {
      sorting,
      rowSelection,
      columnFilters,
      ...(resolvedPagination && { pagination: resolvedPagination }),
    },

    // Handle pagination changes - only override when external handlers exist,
    // otherwise let react-table manage pagination state internally
    ...((onPaginationChange || useSimplePagination) && {
      onPaginationChange: (updater: any) => {
        const currentPagination = resolvedPagination ?? { pageIndex: 0, pageSize: 10 };
        const newPagination =
          typeof updater === 'function' ? updater(currentPagination) : updater;

        if (onPaginationChange) {
          onPaginationChange(newPagination);
        } else if (useSimplePagination) {
          if (newPagination.pageIndex !== currentPagination.pageIndex) {
            onPageChange?.(newPagination.pageIndex);
          }
          if (newPagination.pageSize !== currentPagination.pageSize) {
            onPageSizeChange?.(newPagination.pageSize);
          }
        }
      },
    }),
  });

  if (isLoading) {
    return (
      <div className="rounded-md border">
        <LoadingSpinner className="py-8" />
      </div>
    );
  }

  // Table container styles for sticky header mode
  const tableContainerStyle = stickyHeader
    ? { maxHeight, overflowY: 'auto' as const }
    : undefined;

  return (
    <div className="space-y-4">
      <div
        className="rounded-md border relative w-full overflow-auto"
        style={tableContainerStyle}
      >
        <table className="w-full caption-bottom text-sm">
          <TableHeader className={stickyHeader ? 'sticky top-0 z-10' : ''}>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className={stickyHeader ? 'bg-background border-b' : 'bg-muted/50'}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  onClick={() => onRowClick?.(row.original)}
                  className={onRowClick ? 'cursor-pointer' : ''}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center text-muted-foreground"
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </table>
      </div>

      <DataTablePagination table={table} totalItems={totalItems} />
    </div>
  );
}
